/**
 * Column Diagram Component
 * Displays data lineage and relationships for a column in a visual diagram
 */
import React, { useEffect, useRef } from 'react';
import { Box, Typography, Paper } from '@mui/material';

interface ColumnDiagramProps {
  columnName: string;
  tableName: string;
  domain: string;
}

interface Node {
  id: string;
  label: string;
  type: 'column' | 'table' | 'dataset' | 'attribute' | 'database';
  x: number;
  y: number;
  color: string;
}

interface Edge {
  from: string;
  to: string;
  label?: string;
}

const ColumnDiagram: React.FC<ColumnDiagramProps> = ({ columnName, tableName, domain }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = 600;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Define nodes based on column name
    const nodes: Node[] = [
      // Left side - Source data
      { id: 'demo', label: 'Demographic\nInformation', type: 'dataset', x: 100, y: 130, color: '#A8D5BA' },
      { id: 'occupation_attr', label: 'Occupation', type: 'attribute', x: 100, y: 180, color: '#FFB366' },
      { id: 'behavior', label: 'Customer\nBehavior Data', type: 'dataset', x: 100, y: 230, color: '#FFD966' },
      { id: 'billing', label: 'Customer\nBilling Dataset', type: 'dataset', x: 100, y: 280, color: '#FFD966' },
      
      // Data Quality Rules
      { id: 'empty', label: `${columnName}:\nEmpty Range`, type: 'attribute', x: 100, y: 330, color: '#A8D5BA' },
      { id: 'null', label: `${columnName}:\nNull Range`, type: 'attribute', x: 100, y: 380, color: '#A8D5BA' },
      { id: 'unique', label: `${columnName}:\nUnique Range`, type: 'attribute', x: 100, y: 430, color: '#A8D5BA' },
      { id: 'datatype', label: `${columnName}:\nData Type\nRange`, type: 'attribute', x: 100, y: 490, color: '#A8D5BA' },
      
      // Center - Main column
      { id: 'main', label: columnName.toUpperCase(), type: 'column', x: 320, y: 180, color: '#FFD966' },
      
      // Right side - Target tables
      { id: 'customer_data', label: tableName.toUpperCase(), type: 'table', x: 520, y: 180, color: '#FFD966' },
      { id: 'database', label: 'E-UNIFY\nDatabase', type: 'database', x: 720, y: 180, color: '#FFB366' }
    ];

    // Define edges (relationships)
    const edges: Edge[] = [
      { from: 'occupation_attr', to: 'main', label: 'populates' },
      { from: 'behavior', to: 'main', label: 'contains' },
      { from: 'empty', to: 'main', label: 'validates' },
      { from: 'null', to: 'main', label: 'validates' },
      { from: 'unique', to: 'main', label: 'validates' },
      { from: 'datatype', to: 'main', label: 'validates' },
      { from: 'main', to: 'customer_data', label: 'part of' },
      { from: 'customer_data', to: 'database', label: 'part of' }
    ];

    // Draw edges first (so they appear behind nodes)
    ctx.strokeStyle = '#666';
    ctx.lineWidth = 2;
    ctx.font = '11px Arial';
    ctx.fillStyle = '#666';

    edges.forEach(edge => {
      const fromNode = nodes.find(n => n.id === edge.from);
      const toNode = nodes.find(n => n.id === edge.to);
      
      if (fromNode && toNode) {
        // Draw line
        ctx.beginPath();
        ctx.moveTo(fromNode.x + 80, fromNode.y + 20);
        ctx.lineTo(toNode.x - 10, toNode.y + 20);
        ctx.stroke();

        // Draw arrow
        const angle = Math.atan2(toNode.y - fromNode.y, toNode.x - fromNode.x);
        ctx.beginPath();
        ctx.moveTo(toNode.x - 10, toNode.y + 20);
        ctx.lineTo(toNode.x - 20, toNode.y + 15);
        ctx.lineTo(toNode.x - 20, toNode.y + 25);
        ctx.closePath();
        ctx.fill();

        // Draw label
        if (edge.label) {
          const midX = (fromNode.x + toNode.x) / 2;
          const midY = (fromNode.y + toNode.y) / 2;
          ctx.fillText(edge.label, midX - 20, midY + 10);
        }
      }
    });

    // Draw nodes
    nodes.forEach(node => {
      // Draw rectangle
      ctx.fillStyle = node.color;
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;
      
      const width = 150;
      const height = node.label.includes('\n') ? 50 : 40;
      
      ctx.fillRect(node.x, node.y, width, height);
      ctx.strokeRect(node.x, node.y, width, height);

      // Draw icon based on type
      ctx.fillStyle = '#333';
      ctx.font = 'bold 16px Arial';
      let icon = '';
      switch (node.type) {
        case 'column': icon = '📋'; break;
        case 'table': icon = '📊'; break;
        case 'dataset': icon = '📁'; break;
        case 'attribute': icon = '🏷️'; break;
        case 'database': icon = '🗄️'; break;
      }
      ctx.fillText(icon, node.x + 5, node.y + 25);

      // Draw text
      ctx.font = 'bold 12px Arial';
      ctx.fillStyle = '#000';
      const lines = node.label.split('\n');
      lines.forEach((line, i) => {
        ctx.fillText(line, node.x + 30, node.y + 20 + (i * 15));
      });
    });

    // Draw legend
    const legendX = canvas.width - 180;
    const legendY = 20;
    
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#ccc';
    ctx.fillRect(legendX, legendY, 160, 140);
    ctx.strokeRect(legendX, legendY, 160, 140);
    
    ctx.fillStyle = '#000';
    ctx.font = 'bold 14px Arial';
    ctx.fillText('Legend', legendX + 10, legendY + 20);
    
    const legendItems = [
      { icon: '📋', label: 'Column', color: '#FFD966' },
      { icon: '📊', label: 'Table', color: '#FFD966' },
      { icon: '📁', label: 'Data Set', color: '#FFD966' },
      { icon: '🏷️', label: 'Data Attribute', color: '#FFB366' },
      { icon: '🗄️', label: 'Database', color: '#FFB366' }
    ];
    
    legendItems.forEach((item, i) => {
      const y = legendY + 40 + (i * 20);
      ctx.fillStyle = item.color;
      ctx.fillRect(legendX + 10, y - 12, 15, 15);
      ctx.strokeRect(legendX + 10, y - 12, 15, 15);
      ctx.fillText(item.icon, legendX + 30, y);
      ctx.font = '12px Arial';
      ctx.fillStyle = '#000';
      ctx.fillText(item.label, legendX + 50, y);
      ctx.font = 'bold 14px Arial';
    });

  }, [columnName, tableName, domain]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Data Lineage Diagram
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Visual representation of data relationships, sources, and quality rules for this column.
      </Typography>
      
      <Paper 
        sx={{ 
          p: 2, 
          bgcolor: '#f5f5f5',
          overflow: 'auto'
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: '600px',
            display: 'block'
          }}
        />
      </Paper>

      <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Diagram Controls:
        </Typography>
        <Typography variant="caption" color="text.secondary">
          • This diagram shows data flow from source systems (left) through the column (center) to target tables (right)
          <br />
          • Green boxes represent data quality rules and validations
          <br />
          • Orange boxes represent data attributes and databases
          <br />
          • Yellow boxes represent tables and datasets
        </Typography>
      </Box>
    </Box>
  );
};

export default ColumnDiagram;
